import React, { useState, useMemo, useEffect } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const HelpModal = ({ onClose, userType = "buyer" }) => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [helpData, setHelpData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    // Lazy load help data when modal opens
    useEffect(() => {
        const loadHelpData = async () => {
            try {
                setIsLoading(true);
                const data = await import("./helpData.json");
                setHelpData(data.default);
            } catch (error) {
                console.error("Error loading help data:", error);
                setHelpData({});
            } finally {
                setIsLoading(false);
            }
        };

        loadHelpData();
    }, []);

    // Get help items based on user type
    const helpItems = useMemo(() => {
        if (!helpData) return [];
        return helpData[userType] || [];
    }, [helpData, userType]);

    // Filter items based on search term using regex
    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) return helpItems;
        
        try {
            const regex = new RegExp(searchTerm, "i");
            return helpItems.filter((item) => regex.test(item.title));
        } catch (error) {
            // If regex is invalid, fall back to simple includes
            return helpItems.filter((item) =>
                item.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
    }, [helpItems, searchTerm]);

    const listToShow = searchTerm ? filteredItems : helpItems;

    // Render content based on section type
    const renderSection = (section, index) => {
        switch (section.type) {
            case "paragraph":
                return <p key={index}>{section.content}</p>;
            
            case "heading":
                const HeadingTag = `h${section.level || 4}`;
                return React.createElement(HeadingTag, { key: index }, section.content);
            
            case "orderedList":
                return (
                    <ol key={index}>
                        {section.items.map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ol>
                );
            
            case "unorderedList":
                return (
                    <ul key={index}>
                        {section.items.map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>
                );
            
            case "code":
                return (
                    <pre
                        key={index}
                        style={{
                            background: "#f4f4f4",
                            padding: "10px",
                            borderRadius: "8px",
                            overflowX: "auto",
                        }}
                    >
                        {section.content}
                    </pre>
                );
            
            case "image":
                return (
                    <div
                        key={index}
                        style={{
                            margin: "15px 0",
                            textAlign: "center",
                        }}
                    >
                        <img
                            src={section.src}
                            alt={section.alt || "Help image"}
                            style={{
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                cursor: "pointer",
                                transition: "transform 0.2s",
                            }}
                            onClick={() => {
                                setSelectedImage({
                                    src: section.src,
                                    alt: section.alt,
                                    caption: section.caption
                                });
                                setImageModalOpen(true);
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = "scale(1.02)";
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = "scale(1)";
                            }}
                            onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "block";
                            }}
                        />
                        <div
                            style={{
                                display: "none",
                                padding: "20px",
                                background: "#f5f5f5",
                                borderRadius: "8px",
                                color: "#666",
                                fontSize: "14px",
                            }}
                        >
                            Image not available: {section.alt}
                        </div>
                        {section.caption && (
                            <p
                                style={{
                                    fontSize: "13px",
                                    color: "#666",
                                    marginTop: "8px",
                                    fontStyle: "italic",
                                }}
                            >
                                {section.caption}
                            </p>
                        )}
                    </div>
                );
            
            case "accordion":
                return (
                    <Accordion key={index}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            {section.title}
                        </AccordionSummary>
                        <AccordionDetails>
                            {renderAccordionContent(section.content)}
                        </AccordionDetails>
                    </Accordion>
                );
            
            default:
                return null;
        }
    };

    // Render accordion content
    const renderAccordionContent = (content) => {
        if (content.type === "paragraph") {
            return <p>{content.text}</p>;
        } else if (content.type === "orderedList") {
            return (
                <ol>
                    {content.items.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ol>
            );
        } else if (content.type === "unorderedList") {
            return (
                <ul>
                    {content.items.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            );
        }
        return null;
    };

    return (
        <>
            {/* Image Modal */}
            {imageModalOpen && selectedImage && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0, 0, 0, 0.9)",
                        zIndex: 999999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px",
                    }}
                    onClick={() => setImageModalOpen(false)}
                >
                    <div
                        style={{
                            position: "relative",
                            maxWidth: "90%",
                            maxHeight: "90%",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setImageModalOpen(false)}
                            style={{
                                position: "absolute",
                                top: "-40px",
                                right: "0",
                                background: "transparent",
                                border: "none",
                                color: "white",
                                fontSize: "36px",
                                cursor: "pointer",
                                padding: "0",
                                lineHeight: "1",
                            }}
                        >
                            ×
                        </button>
                        <img
                            src={selectedImage.src}
                            alt={selectedImage.alt}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "85vh",
                                borderRadius: "8px",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                            }}
                        />
                        {selectedImage.caption && (
                            <p
                                style={{
                                    color: "white",
                                    textAlign: "center",
                                    marginTop: "15px",
                                    fontSize: "14px",
                                }}
                            >
                                {selectedImage.caption}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Help Modal */}
            <div
                style={{
                    position: "fixed",
                    bottom: 0,
                    right: "10px",
                    width: "420px",
                    height: "70vh",
                    background: "#fff",
                    boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
                    padding: "20px",
                    zIndex: 99999,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "5px",
                }}
            >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "15px",
                }}
            >
                {selectedItem && (
                    <ArrowBackIcon
                        onClick={() => setSelectedItem(null)}
                        style={{
                            cursor: "pointer",
                            marginRight: "10px",
                            color: "#333",
                            fontSize: "22px",
                        }}
                    />
                )}

                <h2 style={{ flexGrow: 1, margin: 0, fontSize: "18px", fontWeight: 600 }}>
                    {selectedItem ? selectedItem.title : "Help Resources"}
                </h2>

                <button
                    onClick={onClose}
                    className="close-button"
                    style={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        fontSize: "20px !important",
                        color: "#555",
                        width: "25px",
                    }}
                >
                    ×
                </button>
            </div>

            {!selectedItem && (
                <>
                    {isLoading ? (
                        <div
                            style={{
                                flexGrow: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#666",
                            }}
                        >
                            Loading help resources...
                        </div>
                    ) : (
                        <ul
                            style={{
                                listStyle: "none",
                                padding: 0,
                                flexGrow: 1,
                                overflowY: "auto",
                            }}
                        >
                            {listToShow.length > 0 ? (
                                listToShow.map((item, index) => (
                                    <li
                                        key={index}
                                        onClick={() => setSelectedItem(item)}
                                        style={{
                                            padding: "10px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            cursor: "pointer",
                                            borderRadius: "8px",
                                            transition: "background 0.2s",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.background = "#f1f3f4")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.background = "transparent")
                                        }
                                    >
                                        <DescriptionIcon style={{ color: "#1a73e8" }} />
                                        <span style={{ fontSize: "14px", color: "#202124" }}>
                                            {item.title}
                                        </span>
                                    </li>
                                ))
                            ) : (
                                <div
                                    style={{
                                        padding: "20px",
                                        textAlign: "center",
                                        color: "#666",
                                    }}
                                >
                                    No help items found matching "{searchTerm}"
                                </div>
                            )}
                        </ul>
                    )}
                    
                    {/* <div 
                        className="download-help"
                        onClick={() => {
                            const pdfUrl = userType === "globalpartner" 
                                ? "https://amtplfs-devnew-02128f878d874-devlab.s3.ap-south-1.amazonaws.com/public/images/help/GLOBAL+TRADING+PARTNER+LOGIN+User+manual-V2.0+1.pdf"
                                : "https://amtplfs-devnew-02128f878d874-devlab.s3.ap-south-1.amazonaws.com/public/images/help/BUYER+LOGIN+USER+MANUAL-V4.0+1.pdf";
                            
                            const fileName = userType === "globalpartner"
                                ? "Global_Partner_User_Manual.pdf"
                                : "Buyer_User_Manual.pdf";
                            
                            // Create a temporary link and trigger download
                            const link = document.createElement('a');
                            link.href = pdfUrl;
                            link.download = fileName;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }}
                        style={{
                            cursor: "pointer",
                            textAlign: "center",
                            padding: "10px",
                            color: "#1a73e8",
                            fontSize: "14px",
                            fontWeight: 500,
                            transition: "color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.color = "#0d47a1";
                            e.target.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.color = "#1a73e8";
                            e.target.style.textDecoration = "none";
                        }}
                    >
                        Download help
                    </div> */}

                    <div style={{ borderTop: "1px solid #eee", paddingTop: "10px" }}>
                        <div style={{ position: "relative", width: "100%" }}>
                            <input
                                type="text"
                                placeholder="Search Help"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "10px 10px 10px 35px",
                                    borderRadius: "20px",
                                    border: "1px solid #ccc",
                                    outline: "none",
                                    fontSize: "14px",
                                }}
                            />
                            <SearchIcon
                                style={{
                                    position: "absolute",
                                    left: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#999",
                                }}
                            />
                        </div>
                    </div>
                </>
            )}

            {selectedItem && (
                <div
                    style={{
                        flexGrow: 1,
                        overflowY: "auto",
                        paddingRight: "5px",
                    }}
                >
                    {selectedItem.sections.map((section, index) =>
                        renderSection(section, index)
                    )}
                </div>
            )}
            </div>
        </>
    );
};

export default HelpModal;
